import type {
  ComposeHookContribution,
  MessageActionContribution,
  PluginContribution,
  PluginManifest,
  RegisteredPlugin,
  SidebarPanelContribution,
} from './types';

/**
 * In-memory plugin registry — the host side of the extension points.
 *
 * NO IMPLEMENTATION: this records manifests and contributions and lets the UI
 * query them. Sandboxing, bundle loading, permission enforcement and
 * persistence are deliberately out of scope until the plugin system is built
 * for real; each is marked below.
 */
export class PluginRegistry {
  private readonly plugins = new Map<string, RegisteredPlugin>();
  private readonly listeners = new Set<() => void>();

  /** Register a manifest. TODO(plugins): validate `engine` against the host version. */
  register(manifest: PluginManifest): RegisteredPlugin {
    if (this.plugins.has(manifest.id)) {
      throw new Error(`Plugin "${manifest.id}" is already registered`);
    }
    const record: RegisteredPlugin = { manifest, state: 'registered', contributions: [] };
    this.plugins.set(manifest.id, record);
    this.emit();
    return record;
  }

  unregister(pluginId: string): void {
    this.plugins.delete(pluginId);
    this.emit();
  }

  /**
   * Record a contribution made during activation.
   * TODO(plugins): reject contributions the manifest did not declare in
   * `contributes`, and calls that the manifest's `permissions` do not cover.
   */
  contribute(pluginId: string, contribution: PluginContribution): () => void {
    const record = this.plugins.get(pluginId);
    if (!record) throw new Error(`Unknown plugin "${pluginId}"`);
    record.contributions.push(contribution);
    record.state = 'active';
    this.emit();
    return () => {
      const index = record.contributions.indexOf(contribution);
      if (index >= 0) record.contributions.splice(index, 1);
      this.emit();
    };
  }

  list(): RegisteredPlugin[] {
    return [...this.plugins.values()];
  }

  get(pluginId: string): RegisteredPlugin | undefined {
    return this.plugins.get(pluginId);
  }

  sidebarPanels(): SidebarPanelContribution[] {
    return this.contributionsOfKind('sidebar-panel').sort(
      (a, b) => (a.order ?? 100) - (b.order ?? 100),
    );
  }

  messageActions(): MessageActionContribution[] {
    return this.contributionsOfKind('message-action');
  }

  composeHooks(stage: ComposeHookContribution['stage']): ComposeHookContribution[] {
    return this.contributionsOfKind('compose-hook').filter((hook) => hook.stage === stage);
  }

  /** React's useSyncExternalStore-compatible subscription. */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private contributionsOfKind<K extends PluginContribution['kind']>(
    kind: K,
  ): Extract<PluginContribution, { kind: K }>[] {
    const result: Extract<PluginContribution, { kind: K }>[] = [];
    for (const record of this.plugins.values()) {
      if (record.state !== 'active') continue;
      for (const contribution of record.contributions) {
        if (contribution.kind === kind) {
          result.push(contribution as Extract<PluginContribution, { kind: K }>);
        }
      }
    }
    return result;
  }

  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}

/** App-wide registry. TODO(plugins): load installed plugins here at boot. */
export const pluginRegistry = new PluginRegistry();
